import { Alert, AutoComplete, Button, InputNumber, message } from 'antd'
import Table, { ColumnsType } from 'antd/lib/table'
import { SaveOutlined } from '@ant-design/icons'
import React, { useEffect } from 'react'
import axios from 'axios'

export interface ApeCode {
  ape_code: string
  libelleSuggested: string[]
  libelleCustom: string
  libelleInit: string
  libelleEffective?: string
  txAchatsAccessiblesInit?: number
  txAchatsAccessibles?: number
  orgaDataId?: string
  deletable?: boolean
}

interface ApeLabelsProps {
  apeCode: ApeCode[]
  orgaId: string
}

function normalizeApeCodes(apeCode: ApeCode[]): ApeCode[] {
  return apeCode.map((e) => ({
    ...e,
    libelleEffective: e.libelleCustom != '' ? e.libelleCustom : 'Autres métiers',
    txAchatsAccessiblesInit: e.txAchatsAccessibles ? e.txAchatsAccessibles : 0,
    txAchatsAccessibles: e.txAchatsAccessibles ? e.txAchatsAccessibles : 0,
  }))
}

function onLibelleChange(
  value: string,
  apeCode: string,
  setApeCodes: React.Dispatch<React.SetStateAction<ApeCode[]>>,
  apeCodes: ApeCode[],
) {
  const temp = Array.from(apeCodes)
  const index = temp.findIndex((e) => e.ape_code == apeCode)
  temp[index].libelleEffective = value
  setApeCodes(temp)
}

async function saveApe(
  setApeCodes: React.Dispatch<React.SetStateAction<ApeCode[]>>,
  apeCodes: ApeCode[],
  orgaId: string,
) {
  const linesToSaveApe = apeCodes
    .filter((e) => e.libelleEffective && e.libelleEffective != e.libelleCustom)
    .map((e) => ({
      apeCode: e.ape_code,
      libelle: e.libelleEffective == 'Autres métiers' ? undefined : e.libelleEffective,
      txAchatsAccessibles: e.txAchatsAccessibles,
    }))

  if (linesToSaveApe.length == 0) {
    return
  }

  try {
    await axios.post(
      `/organisations/${orgaId}/datacnegoce/apedata`,
      linesToSaveApe.filter((e) => e.libelle != undefined),
    )
    const temp = Array.from(apeCodes)
    linesToSaveApe.forEach((e) => {
      const index = temp.findIndex((t) => e.apeCode == t.ape_code)
      temp[index].libelleCustom = e.libelle || ''
    })
    setApeCodes(temp)
    message.success('Enregistrement effectué avec succès')
  } catch (error: any) {
    message.error(
      `Erreur ${error?.response?.status || ''}, ${error?.response?.statusText || 'enregistrement APE'}`,
    )
  }
}

async function saveTxAchatsAccessibles(
  setApeCodes: React.Dispatch<React.SetStateAction<ApeCode[]>>,
  apeCodes: ApeCode[],
  orgaId: string,
) {
  const linesToSaveTx = apeCodes
    .filter((e) => e.txAchatsAccessiblesInit != e.txAchatsAccessibles)
    .map((e) => ({
      apeCode: e.ape_code,
      metier: e.libelleEffective,
      txAchatsAccessibles: e.txAchatsAccessibles,
      orgaDataId: e.orgaDataId,
    }))

  if (linesToSaveTx.length == 0) {
    return
  }

  try {
    await axios.post(`/organisations/${orgaId}/datacnegoce/txachatsaccessibles`, linesToSaveTx)
    const temp = Array.from(apeCodes)
    linesToSaveTx.forEach((e) => {
      const index = temp.findIndex((t) => e.apeCode == t.ape_code)
      temp[index].txAchatsAccessiblesInit = e.txAchatsAccessibles
    })
    setApeCodes(temp)
    message.success('Enregistrement réussi pour Tx achats accessibles')
  } catch (error: any) {
    message.error(
      `Erreur ${error?.response?.status || ''}, ${error?.response?.statusText || 'enregistrement taux'}`,
    )
  }
}

async function saveAll(
  setApeCodes: React.Dispatch<React.SetStateAction<ApeCode[]>>,
  apeCodes: ApeCode[],
  orgaId: string,
) {
  await saveApe(setApeCodes, apeCodes, orgaId)
  await saveTxAchatsAccessibles(setApeCodes, apeCodes, orgaId)
}

function hasUnsavedChanges(apeCodes: ApeCode[]): boolean {
  return apeCodes.some((e) => {
    const savedLibelle = e.libelleCustom != '' ? e.libelleCustom : 'Autres métiers'
    return e.libelleEffective != savedLibelle || e.txAchatsAccessiblesInit != e.txAchatsAccessibles
  })
}

export default function CnApeLabels(props: ApeLabelsProps) {
  const [apeCodes, setApeCodes] = React.useState<ApeCode[]>(() => normalizeApeCodes(props.apeCode))

  useEffect(() => {
    setApeCodes(normalizeApeCodes(props.apeCode))
  }, [props.apeCode])

  const unsaved = hasUnsavedChanges(apeCodes)

  const columns: ColumnsType<ApeCode> = [
    {
      title: 'Code APE',
      dataIndex: 'ape_code',
      key: 'ape_code',
    },
    {
      title: 'Libellé initial',
      dataIndex: 'libelleInit',
      key: 'libelleInit',
    },
    {
      title: 'Libellé effectif',
      dataIndex: 'libelleCustom',
      key: 'libelleCustom',
      render: (_, { libelleEffective, ape_code, libelleInit, libelleSuggested }) => (
        <AutoComplete
          allowClear
          style={{ minWidth: '200px', color: libelleEffective == 'Autres métiers' ? 'red' : 'black' }}
          placeholder="Libellé"
          value={libelleEffective}
          onChange={(e) => onLibelleChange(e, ape_code, setApeCodes, apeCodes)}
          onSearch={(e) => onLibelleChange(e, ape_code, setApeCodes, apeCodes)}
          options={[libelleInit]
            .concat(libelleSuggested)
            .map((e) => ({ value: e }))
            .filter((e) => e.value != libelleEffective)}
        />
      ),
    },
    {
      title: 'Taux achats accessibles (%)',
      dataIndex: 'txAchatsAccessibles',
      key: 'txAchatsAccessibles',
      render: (_, { txAchatsAccessibles, ape_code }) => (
        <InputNumber
          value={txAchatsAccessibles}
          min={0}
          onChange={(e) => {
            const temp = Array.from(apeCodes)
            const index = temp.findIndex((row) => row.ape_code == ape_code)
            temp[index].txAchatsAccessibles = e || 0
            setApeCodes(temp)
          }}
        />
      ),
    },
    {
      title: '',
      dataIndex: 'deletable',
      key: 'deletable',
      render: (_, { deletable, ape_code }) =>
        deletable ? (
          <Button
            danger
            onClick={async () => {
              if (window.confirm('Êtes-vous sûr de vouloir supprimer ce libellé personnalisé ?')) {
                const temp = Array.from(apeCodes)
                setApeCodes(temp.filter((e) => e.ape_code != ape_code))
                try {
                  await axios.delete(
                    `/organisations/${props.orgaId}/datacnegoce/apedata/${ape_code}`,
                  )
                  message.success('Suppression réussie')
                } catch (error: any) {
                  message.error(
                    `Erreur ${error?.response?.status || ''}, ${error?.response?.statusText || 'suppression'}`,
                  )
                }
              }
            }}
          >
            Supprimer
          </Button>
        ) : null,
    },
  ]

  return (
    <div>
      <Alert message="Paramétrage des libellés affichés dans la colonne 'Métier' + du taux d'achats accessibles utilisés pour les codes APE donnés" type="info" showIcon style={{ marginBottom: '10px' }} />
      {unsaved && (
        <Alert
          message='Modifications non enregistrées — cliquez sur "Enregistrer tout" pour les sauvegarder.'
          type="warning"
          showIcon
          style={{ marginBottom: '10px' }}
        />
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          icon={<SaveOutlined />}
          type="primary"
          shape="round"
          disabled={!unsaved}
          onClick={() => saveAll(setApeCodes, apeCodes, props.orgaId)}
        >
          Enregistrer tout
        </Button>
      </div>
      <br />
      <br />
      <Table
        columns={columns}
        dataSource={apeCodes}
        rowKey="ape_code"
        pagination={false}
        style={{ marginBottom: '10px' }}
      />
    </div>
  )
}
